import { Box, Button, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { District } from 'src/shared/models/district/district';
import { getDistrictById, updateDistrict } from 'src/shared/services/districtService';
import { FormProvider, useForm } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { useGlobals } from 'src/shared/contexts/appContext';
import { toast } from 'react-toastify';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { getOrganizationDistrictId } from 'src/shared/functions/organizationHelpers';
import { DistrictRequest } from '../models/districtRequest';

export const DistrictDetailPage = () => {
  const { t } = useTranslation(['district']);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const { currentUser } = useGlobals();
  const params = useParams();
  const currentDistrictId = getOrganizationDistrictId(currentUser, params.id);

  const isMounted = useIsMounted();

  const [, setDistrict] = useState<District>();

  const requiredMessage = t('common:validationMessages.required');

  const DistrictRequestSchema: Schema<DistrictRequest> = yup.object().shape({
    // copyToEmails: yup.string().email().optional(),
    countyAss: yup.string().required(requiredMessage),
    shortName: yup.string().optional(),
    cOaddress: yup.string().optional(),
    address: yup.string().optional(),
    addressLocal: yup.string().optional(),
    postNr: yup.string().optional(),
    postAddress: yup.string().optional(),
    homesite: yup.string().optional(),
    phone: yup.string().optional(),
    phoneOther: yup.string().optional(),
    email: yup.string().optional(),
    fee: yup.string().optional(),
    bankGiro: yup.string().optional(),
    postGiro: yup.string().optional(),
    orgNr: yup.string().optional(),
    customerNr: yup.string().optional(),
    comment: yup.string().optional(),
  });

  const formMethods = useForm<DistrictRequest>({
    resolver: yupResolver(DistrictRequestSchema),
    mode: 'all',
    defaultValues: {
      countyAss: '',
      shortName: '',
      cOaddress: '',
      address: '',
      addressLocal: '',
      postNr: '',
      postAddress: '',
      homesite: '',
      phone: '',
      phoneOther: '',
      email: '',
      fee: '',
      bankGiro: '',
      postGiro: '',
      orgNr: '',
      customerNr: '',
      comment: '',
    },
  });

  const { handleSubmit, trigger, reset } = formMethods;

  const saveFormData = useCallback(
    async (data: DistrictRequest) => {
      setLoading(true);
      try {
        const res = await updateDistrict(data);
        if (isMounted()) {
          ConsoleHelper.log('res updateDistrict', res);
          toast.success(<>{t('gift:createDonor.saveSuccess')}</>);
          navigate(`${RoutesEnum.OrganizationDistricts}`);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('saveFormDataError:', e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [isMounted, navigate, t],
  );
  const onSubmit = async (data: DistrictRequest) => {
    ConsoleHelper.log('onSubmit', data);
    await trigger();
    await saveFormData(data);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getDistrictById(currentDistrictId);
        if (isMounted()) {
          setDistrict(result.data);
          reset(result.data);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };

    fetchData();
  }, [isMounted, currentDistrictId, reset]);

  const districtName = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.CountyLabel, t('member:tableHeaders.district'));

  return (
    <FormLoaderWrapper loading={loading}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: 3 }}>
            <Grid spacing={3} container>
              <Grid item xs={12} lg={7}>
                <Typography variant='body1' sx={{ fontWeight: 600, mb: 2 }}>
                  {districtName}
                </Typography>
                <Grid spacing={2} container>
                  <Grid item xs={12} sm={4}>
                    <ControlledTextField label={t('district:customerNr')} name='customerNr' />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <ControlledTextField label={t('district:name')} required name='countyAss' />
                  </Grid>
                </Grid>
                <Typography variant='body1' sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
                  {t('district:other')}
                </Typography>
                <Grid spacing={2} container>
                  {/* Distriktsavgiften redigeras på fliken Avgift, inte här. Kolumnen bär flera
                      nivåer separerade med komma, och ett fritextfält på uppgiftssidan gick förbi
                      periodhanteringen och kunde skriva sönder familjemedlemsavgiften. Fältet
                      ligger kvar i formuläret så att värdet skickas tillbaka oförändrat. */}
                  <Grid item xs={6} md={4}>
                    <ControlledTextField label={t('district:postGiro')} name='postGiro' />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <ControlledTextField label={t('district:bankGiro')} name='bankGiro' />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <ControlledTextField label={t('district:orgNr')} name='orgNr' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField label={t('district:comment')} multiline rows={4} name='comment' />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} lg={5}>
                <Typography variant='body1' sx={{ fontWeight: 600, mb: 2 }}>
                  {t('district:contact')}
                </Typography>
                <Grid spacing={2} container>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('district:address')} name='address' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('district:coAddress')} name='cOaddress' />
                  </Grid>
                  <Grid item xs={4}>
                    <ControlledTextField label={t('district:postNr')} name='postNr' />
                  </Grid>
                  <Grid item xs={8}>
                    <ControlledTextField label={t('district:postAddress')} name='postAddress' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField label={t('district:addressLocal')} name='addressLocal' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField label={t('district:email')} name='email' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('district:phone')} name='phone' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('district:phoneOther')} name='phoneOther' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField label={t('district:homesite')} name='homesite' />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type='submit' variant='contained' disableElevation>
                  {t('common:button.save')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </form>
      </FormProvider>
    </FormLoaderWrapper>
  );
};
